import { QueryKey, useQueryClient } from 'react-query'
import { useStrategy } from '../../hooks/useStrategy'
import { LinkingItemsIds, LinkingItemType } from '../../../components/Linking/Linking.types'
import { getActivitiesQueryKey } from '../useGetActivitiesQuery'
import { getOrgActivitiesQueryKey } from '../useGetOrgActivitiesQuery'
import { getEvidenceQueryKey } from '../useGetEvidenceQuery'
import { getValueMessageLinkedDataQueryKey } from '../useGetValueMessageLinkedDataQuery'
import { getStrategicImperativesQueryKey } from '../strategicImperatives/useStrategicImperativesQuery'
import { getValueNarrativesQueryKey } from '../../../components/ValueNarrativesView/queries/useGetValueNarrativesQuery'
import { getTppCellsQueryKey } from '../../../components/TppView/hooks/useTppData'

const useInvalidateLinkingQueries = () => {
    const queryClient = useQueryClient()

    const { _id: strategyId, organizationId } = useStrategy()

    const getQueryKeysShowingType = (linkingType: LinkingItemType): QueryKey[] => {
        switch (linkingType) {
            case 'activity':
                return [getActivitiesQueryKey(strategyId), getOrgActivitiesQueryKey(organizationId)]
            case 'evidence':
                return [getEvidenceQueryKey(strategyId)]
            case 'valueMessage':
                return [getValueNarrativesQueryKey(strategyId), getValueMessageLinkedDataQueryKey()]
            case 'strategicImperative':
                return [getStrategicImperativesQueryKey(strategyId)]
            case 'tppCell':
                return [getTppCellsQueryKey()]
            default:
                return []
        }
    }

    const invalidateQueriesShowingTypes = async (linkingTypes: LinkingItemType[]) => {
        await Promise.all(
            [...new Set(linkingTypes)]
                .flatMap(getQueryKeysShowingType)
                .map((queryKey) => queryClient.invalidateQueries(queryKey))
        )
    }

    const refreshQueries = (queryKey: QueryKey) => {
        queryClient.removeQueries(queryKey, { inactive: true })
        return queryClient.invalidateQueries(queryKey)
    }

    const invalidateLinkedItemsQuery = (id: string, linkingType: LinkingItemType) => {
        return refreshQueries(['linkedItems', id, linkingType])
    }

    /** Refreshes what a link save changed: the owner's links, each changed item's links, and every list showing either. */
    const invalidateAfterLinkSave = (
        owner: { id: string; type: LinkingItemType },
        { linkedItems, unlinkedItems }: { linkedItems: LinkingItemsIds; unlinkedItems: LinkingItemsIds }
    ) => {
        const changedItems = [linkedItems, unlinkedItems].flatMap((itemsIds) =>
            (Object.keys(itemsIds) as LinkingItemType[]).flatMap((type) => itemsIds[type].map((id) => ({ id, type })))
        )

        return Promise.all([
            queryClient.invalidateQueries(['linkedItems', owner.id, owner.type]),
            ...changedItems.map(({ id, type }) => invalidateLinkedItemsQuery(id, type)),
            invalidateQueriesShowingTypes([owner.type, ...changedItems.map(({ type }) => type)]),
        ])
    }

    const invalidateLinkingQueries = async () => {
        refreshQueries(['linkedItems'])
        refreshQueries(['linkableItems', strategyId])
    }

    return {
        invalidateAfterLinkSave,
        invalidateLinkingQueries,
        invalidateQueriesShowingTypes,
    }
}

export default useInvalidateLinkingQueries
